/**
 * A decision's edits on the draft (Feature 79), each a pure function as the
 * draft's own are.
 *
 * A decision grows from a gate: Add else or Add else if on the gate
 * `from→to` makes the gate's conditions its first branch, named after `to`,
 * and the link that branch's (D3). It shrinks back the moment it has one
 * branch and no else: every link on that branch becomes the gate it was
 * (D12). In switch mode each case is a branch tested by its values instead
 * of its rules; switching back to If keeps each case as a branch testing
 * "field equals value" (the wireframe's choice).
 *
 * Revision 1 draws each branch as a condition node (D26): a link is made by a
 * drag from a condition node, and a gate's and its condition nodes' positions
 * are kept and follow every rename and removal (D32). New names are the
 * gate's step's (`<step>_gate`) and numbered conditions (`condition_2`,
 * `case_2`), never a step's (D30, D31).
 *
 * A gate added from the toolbar (D28) follows nothing until a link is dragged
 * into it from `first` or a step, which is its way in, and a later drag
 * replaces it (D29). Save waits on a gate that follows nothing.
 */
import type {
  LayoutPoint,
  LinkageBranch,
  LinkageDecision,
  LinkageDecisionMode,
  LinkageProblem,
  LinkageRules,
} from '@optum/api-client/types';

import {
  ELSE,
  conditionId,
  conditionsOf,
  findCondition,
  parseConditionId,
  withoutConditionKeys,
} from './conditions.js';
import {
  FIRST,
  connect,
  connectRefusal,
  positions,
  upstream,
  type ConnectRefusal,
  type Draft,
  type DraftLink,
} from './draft.js';
import { COLUMN, GATE_COLUMN, besideGate } from './layout.js';
import { isCall } from './start.js';

const MAX_NAME = 64;

function emptyRules(): LinkageRules {
  return { join: 'all', items: [] };
}

/** The decision of that name, or undefined. */
export function decisionOf(draft: Draft, name: string): LinkageDecision | undefined {
  return draft.decisions.find((d) => d.name === name);
}

/** A name free among the draft's nodes, made from `base`. */
function freeName(draft: Draft, base: string): string {
  const taken = new Set([
    FIRST,
    ...draft.steps.map((s) => s.name),
    ...draft.decisions.map((d) => d.name),
  ]);
  const stem = base.slice(0, MAX_NAME - 3);
  if (!taken.has(stem)) return stem;
  let n = 2;
  while (taken.has(`${stem}_${n}`)) n += 1;
  return `${stem}_${n}`;
}

/** The next numbered branch name free within one decision: `condition_3`, `case_2` (D31). */
function numberedBranch(decision: LinkageDecision, stem: 'condition' | 'case'): string {
  const taken = new Set([ELSE, ...decision.branches.map((b) => b.name)]);
  let n = decision.branches.length + 1;
  while (taken.has(`${stem}_${n}`)) n += 1;
  return `${stem}_${n}`;
}

/** A layout with each key renamed by `rename`, its points kept. */
function rekeyed<T>(layout: Record<string, T>, rename: (key: string) => string): Record<string, T> {
  const moved: Record<string, T> = {};
  for (const [key, point] of Object.entries(layout)) moved[rename(key)] = point;
  return moved;
}

function withDecision(
  draft: Draft,
  name: string,
  change: (decision: LinkageDecision) => LinkageDecision,
): Draft {
  return { ...draft, decisions: draft.decisions.map((d) => (d.name === name ? change(d) : d)) };
}

function branchLink(decision: string, branch: string, to: string): DraftLink {
  return { from: decision, to, branch, rules: emptyRules(), auto_pass: false };
}

/**
 * Add else (`kind` else) or Add else if on the gate `from→to`, or the draft
 * unchanged when there is no such gate; with the new decision's name.
 */
export function grow(
  draft: Draft,
  from: string,
  to: string,
  kind: 'else' | 'else_if',
): { draft: Draft; name: string | null } {
  const link = draft.links.find((l) => l.from === from && l.to === to && l.branch === undefined);
  if (!link) return { draft, name: null };
  const name = freeName(draft, `${to}_gate`);
  const branches: LinkageBranch[] = [{ name: to, rules: link.rules }];
  if (kind === 'else_if') branches.push({ name: 'condition_2', rules: emptyRules() });
  const decision: LinkageDecision = { name, from, mode: 'if', branches, else: kind === 'else' };
  // The gate takes the column after its source and its condition nodes the
  // next; every node from there on moves right to make room for both (D30).
  const at = positions(draft);
  const source = at[from] ?? { x: 0, y: 0 };
  const layout: Draft['layout'] = {};
  for (const [node, point] of Object.entries(at)) {
    layout[node] = point.x > source.x ? { ...point, x: point.x + COLUMN + GATE_COLUMN } : point;
  }
  const gate = { x: source.x + COLUMN, y: source.y };
  layout[name] = gate;
  conditionsOf(decision).forEach((c, i, all) => {
    layout[c.id] = besideGate(gate, i, all.length);
  });
  return {
    draft: {
      ...draft,
      links: draft.links.map((l) => (l === link ? branchLink(name, to, to) : l)),
      decisions: [...draft.decisions, decision],
      layout,
    },
    name,
  };
}

/**
 * A gate from the toolbar (D28): an if with no condition yet and an else, or
 * a case with no value yet and a default. It follows nothing yet (D29), and
 * stands at `at` with its condition nodes beside it.
 */
export function addGate(
  draft: Draft,
  mode: LinkageDecisionMode,
  at: LayoutPoint,
): { draft: Draft; name: string } {
  const name = freeName(draft, 'gate');
  const decision: LinkageDecision =
    mode === 'switch'
      ? { name, from: '', mode, ref: '', branches: [{ name: 'case_1', values: [] }], else: true }
      : {
          name,
          from: '',
          mode,
          branches: [{ name: 'condition_1', rules: emptyRules() }],
          else: true,
        };
  const layout = { ...draft.layout, [name]: at };
  conditionsOf(decision).forEach((c, i, all) => {
    layout[c.id] = besideGate(at, i, all.length);
  });
  return { draft: { ...draft, decisions: [...draft.decisions, decision], layout }, name };
}

/** Why `source` may not be a gate's way in, or null when it may (D29). */
export function wayInRefusal(draft: Draft, gate: string, source: string): ConnectRefusal | null {
  const decision = decisionOf(draft, gate);
  if (!decision) return 'unknown';
  // Only `first` or a step leads into a gate (D10).
  if (source !== FIRST && !draft.steps.some((s) => s.name === source)) return 'into_decision';
  if (source === decision.from) return 'duplicate';
  // A step after the gate would close a loop.
  if (upstream(draft, source).has(gate)) return 'loop';
  return null;
}

/**
 * A gate's way in set, or replaced, by a link dragged into it. After a call,
 * every step its conditions lead to is independent (D18).
 */
export function setWayIn(draft: Draft, gate: string, source: string): Draft {
  if (wayInRefusal(draft, gate, source) !== null) return draft;
  const moved = withDecision(draft, gate, (d) => ({ ...d, from: source }));
  if (!(source === FIRST && isCall(draft.start))) return moved;
  const reached = new Set(moved.links.filter((l) => l.from === gate).map((l) => l.to));
  return {
    ...moved,
    steps: moved.steps.map((s) => {
      if (!reached.has(s.name)) return s;
      const { write_back: _dropped, ...rest } = s;
      return { ...rest, mode: 'independent' as const };
    }),
  };
}

/** What Save waits on for gates: a gate that follows nothing yet (D29). */
export function gateProblems(draft: Draft): LinkageProblem[] {
  return draft.decisions
    .filter((d) => d.from === '')
    .map((d) => ({
      where: d.name,
      code: 'gate_follows_nothing',
      message: 'follows nothing yet: drag a link into it from first or a step',
    }));
}

/** A switch case's values as an if branch's rules: the field equals one of them. */
function valuesAsRules(ref: string, values: readonly string[]): LinkageRules {
  return {
    join: values.length > 1 ? 'any' : 'all',
    items: values.map((value) => ({ ref, op: 'equals' as const, value })),
  };
}

/** A decision with one branch and no else shrinks back into the gates it grew from (D12). */
export function shrink(draft: Draft, name: string): Draft {
  const decision = decisionOf(draft, name);
  if (!decision || decision.else || decision.branches.length !== 1) return draft;
  const [branch] = decision.branches as [LinkageBranch];
  const rules =
    decision.mode === 'switch'
      ? valuesAsRules(decision.ref ?? '', branch.values ?? [])
      : (branch.rules ?? emptyRules());
  const layout = withoutConditionKeys(draft.layout, name);
  delete layout[name];
  return {
    ...draft,
    links: draft.links.flatMap((l) => {
      if (l.from !== name) return [l];
      return l.branch === branch.name
        ? [{ from: decision.from, to: l.to, rules, auto_pass: false }]
        : [];
    }),
    decisions: draft.decisions.filter((d) => d.name !== name),
    layout,
  };
}

/** Add an else if (a case, on a switch) after the last branch. */
export function addBranch(draft: Draft, name: string): Draft {
  return withDecision(draft, name, (d) => {
    const branch =
      d.mode === 'switch'
        ? { name: numberedBranch(d, 'case'), values: [] }
        : { name: numberedBranch(d, 'condition'), rules: emptyRules() };
    return { ...d, branches: [...d.branches, branch] };
  });
}

/** A branch and its links removed; the decision shrinks back when that leaves one and no else. */
export function removeBranch(draft: Draft, name: string, branch: string): Draft {
  const without = withDecision(draft, name, (d) => ({
    ...d,
    branches: d.branches.filter((b) => b.name !== branch),
  }));
  return shrink(
    {
      ...without,
      links: without.links.filter((l) => !(l.from === name && l.branch === branch)),
      layout: withoutConditionKeys(without.layout, name, branch),
    },
    name,
  );
}

/** Why a branch may not take this name, or null when it may. */
export function branchRenameRefusal(
  decision: LinkageDecision,
  from: string,
  to: string,
): string | null {
  if (to === from) return null;
  if (!to || to.length > MAX_NAME) return 'one to 64 characters';
  if (to === ELSE) return 'else is the else';
  if (decision.branches.some((b) => b.name === to)) return 'another branch has this name';
  return null;
}

/** A branch renamed, with its links. */
export function renameBranch(draft: Draft, name: string, from: string, to: string): Draft {
  const decision = decisionOf(draft, name);
  if (!decision || branchRenameRefusal(decision, from, to) !== null || from === to) return draft;
  const renamed = withDecision(draft, name, (d) => ({
    ...d,
    branches: d.branches.map((b) => (b.name === from ? { ...b, name: to } : b)),
  }));
  const old = conditionId(name, from);
  return {
    ...renamed,
    links: renamed.links.map((l) =>
      l.from === name && l.branch === from ? { ...l, branch: to } : l,
    ),
    layout: rekeyed(renamed.layout, (key) => (key === old ? conditionId(name, to) : key)),
  };
}

/** A branch moved one place earlier (-1) or later (+1); its order is the order tried. */
export function moveBranch(draft: Draft, name: string, branch: string, by: -1 | 1): Draft {
  return withDecision(draft, name, (d) => {
    const at = d.branches.findIndex((b) => b.name === branch);
    const to = at + by;
    if (at < 0 || to < 0 || to >= d.branches.length) return d;
    const branches = [...d.branches];
    [branches[at], branches[to]] = [branches[to] as LinkageBranch, branches[at] as LinkageBranch];
    return { ...d, branches };
  });
}

/** An if branch's rules. */
export function setBranchRules(
  draft: Draft,
  name: string,
  branch: string,
  rules: LinkageRules,
): Draft {
  return withDecision(draft, name, (d) => ({
    ...d,
    branches: d.branches.map((b) => (b.name === branch ? { ...b, rules } : b)),
  }));
}

/** The else on or off; off takes its links with it and may shrink the decision back. */
export function setElse(draft: Draft, name: string, on: boolean): Draft {
  const toggled = withDecision(draft, name, (d) => ({ ...d, else: on }));
  if (on) return toggled;
  return shrink(
    {
      ...toggled,
      links: toggled.links.filter((l) => !(l.from === name && l.branch === ELSE)),
      layout: withoutConditionKeys(toggled.layout, name, ELSE),
    },
    name,
  );
}

/** If or Switch. If to Switch asks each case for its values; Switch to If tests "field equals value". */
export function setMode(draft: Draft, name: string, mode: LinkageDecisionMode): Draft {
  return withDecision(draft, name, (d) => {
    if (d.mode === mode) return d;
    if (mode === 'switch') {
      return {
        ...d,
        mode,
        ref: '',
        branches: d.branches.map((b) => ({ name: b.name, values: [] })),
      };
    }
    const { ref: _dropped, ...rest } = d;
    return {
      ...rest,
      mode,
      branches: d.branches.map((b) => ({
        name: b.name,
        rules: valuesAsRules(d.ref ?? '', b.values ?? []),
      })),
    };
  });
}

/** The field a switch reads. */
export function setSwitchRef(draft: Draft, name: string, ref: string): Draft {
  return withDecision(draft, name, (d) => ({ ...d, ref }));
}

/** A switch case's values, each kept once and in order. */
export function setCaseValues(
  draft: Draft,
  name: string,
  branch: string,
  values: readonly string[],
): Draft {
  const kept = [...new Set(values.map((v) => v.trim()).filter(Boolean))];
  return withDecision(draft, name, (d) => ({
    ...d,
    branches: d.branches.map((b) => (b.name === branch ? { ...b, values: kept } : b)),
  }));
}

/** The decision's branches with a path to `node`. */
export function branchesReaching(draft: Draft, decision: string, node: string): Set<string> {
  const found = new Set<string>();
  for (const link of draft.links) {
    if (link.from !== decision || link.branch === undefined) continue;
    if (link.to === node || upstream(draft, node).has(link.to)) found.add(link.branch);
  }
  return found;
}

/**
 * A link from a branch's output to a step, or the draft unchanged when it is
 * refused. A step two branches of one decision now reach runs on any (D8,
 * D17); a step after a decision that follows the call is independent (D18).
 */
export function connectBranch(draft: Draft, name: string, branch: string, to: string): Draft {
  const decision = decisionOf(draft, name);
  if (!decision || branchRefusal(draft, name, to) !== null) return draft;
  const linked = { ...draft, links: [...draft.links, branchLink(name, branch, to)] };
  const joined = branchesReaching(linked, name, to).size > 1 || reachedTwice(linked, name, to);
  const afterCall = decision.from === FIRST && isCall(draft.start);
  return {
    ...linked,
    steps: linked.steps.map((s) => {
      if (s.name !== to) return s;
      const merged = joined ? { ...s, merge: 'any' as const } : s;
      if (!afterCall) return merged;
      const { write_back: _dropped, ...rest } = merged;
      return { ...rest, mode: 'independent' as const };
    }),
  };
}

/** Why a branch's output may not link to `to`, or null when it may. */
export function branchRefusal(draft: Draft, name: string, to: string): ConnectRefusal | null {
  const decision = decisionOf(draft, name);
  if (!decision) return 'unknown';
  if (to === FIRST) return 'into_first';
  if (decisionOf(draft, to)) return 'into_decision';
  if (!draft.steps.some((s) => s.name === to)) return 'unknown';
  if (draft.links.some((l) => l.from === name && l.to === to)) return 'duplicate';
  // The decision's source, or anything before it, would close a loop.
  if (to === decision.from || upstream(draft, decision.from).has(to)) return 'loop';
  return null;
}

/**
 * A canvas connection made into a link: from a condition node, its branch's
 * (D26); into a gate, its way in (D29); from a step or `first`, a gated link.
 * Nothing links into a condition node, and a gate leads only to its own
 * condition nodes. The refusal, when there is one.
 */
export function linkFrom(
  draft: Draft,
  source: string,
  target: string,
): { draft: Draft; refusal: ConnectRefusal | null } {
  if (findCondition(draft.decisions, target)) return { draft, refusal: 'into_condition' };
  // A link dragged into a gate is its way in (D29).
  if (decisionOf(draft, target)) {
    const refusal = wayInRefusal(draft, target, source);
    return refusal ? { draft, refusal } : { draft: setWayIn(draft, target, source), refusal: null };
  }
  const from = findCondition(draft.decisions, source);
  if (from) {
    const refusal = branchRefusal(draft, from.decision.name, target);
    if (refusal !== null) return { draft, refusal };
    return {
      draft: connectBranch(draft, from.decision.name, from.condition.branch, target),
      refusal: null,
    };
  }
  if (decisionOf(draft, source)) return { draft, refusal: 'unknown' };
  const refusal = connectRefusal(draft, source, target);
  if (refusal !== null) return { draft, refusal };
  return { draft: connect(draft, source, target), refusal: null };
}

/** Whether a step's links come from steps on two different branches of one decision. */
function reachedTwice(draft: Draft, name: string, to: string): boolean {
  const ways = draft.links
    .filter((l) => l.to === to)
    .map((l) =>
      l.from === name && l.branch ? new Set([l.branch]) : branchesReaching(draft, name, l.from),
    )
    .filter((found) => found.size > 0);
  return ways.some((a, i) => ways.slice(i + 1).some((b) => [...a].every((x) => !b.has(x))));
}

/** A decision renamed, with its links, its position, and its condition nodes' positions. */
export function renameDecision(draft: Draft, from: string, to: string): Draft {
  if (from === to || !decisionOf(draft, from)) return draft;
  const layout = rekeyed(draft.layout, (key) => {
    if (key === from) return to;
    const parsed = parseConditionId(key);
    return parsed?.gate === from ? conditionId(to, parsed.branch) : key;
  });
  return {
    ...draft,
    decisions: draft.decisions.map((d) => (d.name === from ? { ...d, name: to } : d)),
    links: draft.links.map((l) => (l.from === from ? { ...l, from: to } : l)),
    layout,
  };
}

/** A decision removed, with every link out of it and its condition nodes' positions. */
export function removeDecision(draft: Draft, name: string): Draft {
  const layout = withoutConditionKeys(draft.layout, name);
  delete layout[name];
  return {
    ...draft,
    decisions: draft.decisions.filter((d) => d.name !== name),
    links: draft.links.filter((l) => l.from !== name),
    layout,
  };
}
